/**
 * Min/max range checks for search and preference forms (QA run 7 S3/S16,
 * run 5 F19). "Bedrooms 4–2" or "$500k–$300k" used to run or save silently and
 * then match nothing.
 *
 * Messages match the backend's `RANGE_BOUNDS` (app/models/schemas.py) so the
 * inline error and a 422 read the same.
 */

/** Upper-bound key → [lower-bound key, message]. */
export const RANGE_BOUNDS = {
  max_price: ['min_price', "Max price can't be less than min price."],
  max_bedrooms: ['min_bedrooms', "Max bedrooms can't be less than min bedrooms."],
  max_bathrooms: ['min_bathrooms', "Max bathrooms can't be less than min bathrooms."],
  max_sqft: ['min_sqft', "Max square feet can't be less than min square feet."],
  year_built_before: [
    'year_built_after',
    '"Built before" year can\'t be earlier than "built after" year.',
  ],
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Find inverted min/max pairs in a form's values.
 *
 * Blank or non-numeric bounds are ignored, and only keys present in `values`
 * are checked, so each form gets errors for the fields it actually has.
 *
 * @param {Object} values - form state keyed by the API field names
 * @returns {Object} `{ [maxKey]: message }`; empty when every range is valid
 */
export function findRangeErrors(values) {
  const errors = {};
  if (!values) return errors;
  Object.entries(RANGE_BOUNDS).forEach(([maxKey, [minKey, message]]) => {
    const low = toNumber(values[minKey]);
    const high = toNumber(values[maxKey]);
    if (low !== null && high !== null && high < low) {
      errors[maxKey] = message;
    }
  });
  return errors;
}

export const hasRangeErrors = (errors) => Object.keys(errors || {}).length > 0;
